import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AdminShell } from "@/components/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

type Promo = {
  id: string; code: string; description: string | null;
  mode: "stripe" | "app"; discount_type: "percent" | "amount"; discount_value: number;
  applies_to: "booking" | "shop" | "both"; min_subtotal: number;
  max_redemptions: number | null; redemptions_count: number;
  starts_at: string | null; expires_at: string | null; active: boolean;
  stripe_coupon_id: string | null;
};

const blank: Partial<Promo> = {
  code: "", mode: "stripe", discount_type: "percent", discount_value: 10,
  applies_to: "both", min_subtotal: 0, active: true,
};

export default function AdminPromos() {
  const [rows, setRows] = useState<Promo[]>([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState<Partial<Promo> | null>(null);

  async function load() {
    setLoading(true);
    const { data } = await supabase.from("promo_codes").select("*").order("created_at", { ascending: false });
    setRows((data ?? []) as Promo[]);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function save() {
    if (!edit?.code) return toast.error("Code required");
    const payload = { ...edit, code: edit.code.trim().toUpperCase() };
    const { error } = edit.id
      ? await supabase.from("promo_codes").update(payload).eq("id", edit.id)
      : await supabase.from("promo_codes").insert(payload as any);
    if (error) return toast.error(error.message);
    toast.success("Saved"); setEdit(null); load();
  }

  async function del(id: string) {
    if (!confirm("Delete this promo?")) return;
    const { error } = await supabase.from("promo_codes").delete().eq("id", id);
    if (error) return toast.error(error.message);
    load();
  }

  return (
    <AdminShell title="Promo Codes" actions={<Button size="sm" onClick={() => setEdit(blank)}><Plus className="w-4 h-4 mr-1" /> New Code</Button>}>
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
        <div className="bg-surface border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-panel">
              <tr><th className="text-left p-2">Code</th><th className="text-left p-2">Discount</th><th className="text-left p-2">Mode</th><th className="text-left p-2">Applies</th><th className="text-left p-2">Used</th><th className="text-left p-2">Active</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-border">
                  <td className="p-2 font-mono">{r.code}</td>
                  <td className="p-2">{r.discount_type === "percent" ? `${r.discount_value}%` : `$${r.discount_value}`}</td>
                  <td className="p-2">{r.mode}{r.stripe_coupon_id ? ` · ${r.stripe_coupon_id.slice(0, 10)}…` : ""}</td>
                  <td className="p-2">{r.applies_to}</td>
                  <td className="p-2">{r.redemptions_count}{r.max_redemptions ? `/${r.max_redemptions}` : ""}</td>
                  <td className="p-2">{r.active ? "✓" : "—"}</td>
                  <td className="p-2 flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => setEdit(r)}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => del(r.id)}><Trash2 className="w-4 h-4" /></Button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No promo codes yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {edit && (
        <Dialog open onOpenChange={(o) => !o && setEdit(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>{edit.id ? "Edit Promo" : "New Promo"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Code</Label><Input value={edit.code ?? ""} onChange={e => setEdit({ ...edit, code: e.target.value })} /></div>
              <div className="col-span-2"><Label>Description</Label><Input value={edit.description ?? ""} onChange={e => setEdit({ ...edit, description: e.target.value })} /></div>
              <div><Label>Mode</Label>
                <Select value={edit.mode ?? "stripe"} onValueChange={(v) => setEdit({ ...edit, mode: v as any })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stripe">Stripe coupon</SelectItem>
                    <SelectItem value="app">App-side math</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Applies to</Label>
                <Select value={edit.applies_to ?? "both"} onValueChange={(v) => setEdit({ ...edit, applies_to: v as any })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="both">Both</SelectItem>
                    <SelectItem value="booking">Bookings</SelectItem>
                    <SelectItem value="shop">Shop</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Discount type</Label>
                <Select value={edit.discount_type ?? "percent"} onValueChange={(v) => setEdit({ ...edit, discount_type: v as any })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percent</SelectItem>
                    <SelectItem value="amount">Amount (USD)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Value</Label><Input type="number" value={edit.discount_value ?? 0} onChange={e => setEdit({ ...edit, discount_value: Number(e.target.value) })} /></div>
              <div><Label>Min subtotal</Label><Input type="number" value={edit.min_subtotal ?? 0} onChange={e => setEdit({ ...edit, min_subtotal: Number(e.target.value) })} /></div>
              <div><Label>Max redemptions</Label><Input type="number" value={edit.max_redemptions ?? ""} onChange={e => setEdit({ ...edit, max_redemptions: e.target.value ? Number(e.target.value) : null })} /></div>
              <div><Label>Starts</Label><Input type="datetime-local" value={edit.starts_at ? edit.starts_at.slice(0, 16) : ""} onChange={e => setEdit({ ...edit, starts_at: e.target.value ? new Date(e.target.value).toISOString() : null })} /></div>
              <div><Label>Expires</Label><Input type="datetime-local" value={edit.expires_at ? edit.expires_at.slice(0, 16) : ""} onChange={e => setEdit({ ...edit, expires_at: e.target.value ? new Date(e.target.value).toISOString() : null })} /></div>
              <div className="col-span-2 flex items-center gap-2"><Switch checked={!!edit.active} onCheckedChange={(v) => setEdit({ ...edit, active: v })} /> <Label>Active</Label></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setEdit(null)}>Cancel</Button>
              <Button onClick={save}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </AdminShell>
  );
}